// İSG uzmanı portalı — Ayarlar (Settings) erişim sondası (route guard UX).
//
// Asıl yetki backend'dedir: her Ayarlar ucu `[ModuleAction("companies"|"branches"|
// "sites", …)]` taşır; grant'ı olmayan principal → 403 (api-contract §10). Bu sonda
// yalnız UX görünürlüğüdür — Ayarlar'ın çapası olan firma ucunu (`GET /company`,
// `companies/get` yetkili) çeker:
//   - 2xx  → erişim var (granted) — firma verisi de döner (firma ekranı yeniden kullanır)
//   - 403  → yetki yok (forbidden) → gizli menü + 403 sayfası
//   - diğer (404/503/ağ) → servis/veri kullanılamıyor (unavailable) → yeniden dene
// Sorgu anahtarı firma ekranıyla PAYLAŞILIR (`settingsKeys.company()`) — tek istek.
//
// §9 (admin, cross-tenant) sondasından (`useAdminAccess`) AYRIDIR: Ayarlar herhangi
// bir tenant yöneticisine (firma yetkisi olan) açıktır; sistem-admin şartı yoktur.

import { ApiError, type CompanySettingsDto, settingsApi } from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';
import { settingsKeys } from './settingsKeys';

export type SettingsAccessStatus = 'loading' | 'granted' | 'forbidden' | 'unavailable';

export interface SettingsAccess {
  status: SettingsAccessStatus;
  /** Çağıranın firması (yalnız `granted` iken dolu). */
  company: CompanySettingsDto | undefined;
  /** `unavailable` durumunda yeniden denemek için. */
  refetch: () => void;
}

/**
 * Ayarlar erişimini backend sondasıyla belirler. Oturum açık (RequireAuth altı)
 * varsayılır. 4xx'te retry yok (anında sonuç); 403 (yetki yok) ≠ 404/503 (veri/servis)
 * ayrımı korunur.
 */
export function useSettingsAccess(): SettingsAccess {
  const query = useQuery({
    queryKey: settingsKeys.company(),
    queryFn: () => settingsApi.getCompany(),
    retry: false,
    staleTime: 5 * 60_000,
  });

  let status: SettingsAccessStatus;
  if (query.isPending) {
    status = 'loading';
  } else if (query.isSuccess) {
    status = 'granted';
  } else if (query.error instanceof ApiError && query.error.statusCode === 403) {
    status = 'forbidden';
  } else {
    status = 'unavailable';
  }

  return { status, company: query.data, refetch: () => void query.refetch() };
}
